import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { Alert, Empty, Kpi, KpiGrid, Muted, PageHeader, Section, Tabs } from '@/components/ui'
import {
  importarCargosArquivo,
  importarDescritivoCargos,
  importarDicionarioCompetencias,
  type ImportarCargosProgresso,
  type ImportarCargosResult,
} from './api'

type Modo = 'descritivo' | 'competencias' | 'dominio'

type ResultadoImport = ImportarCargosResult & {
  ativos?: number
  duplicados_removidos?: number
}

export function ImportarCargosPage() {
  const { canEdit } = useAuth()
  const editable = canEdit('cargos')
  const qc = useQueryClient()

  const [modo, setModo] = useState<Modo>('descritivo')
  const [file, setFile] = useState<File | null>(null)
  const [dryRun, setDryRun] = useState(true)
  const [criarFaltantes, setCriarFaltantes] = useState(false)
  const [msg, setMsg] = useState('')
  const [erro, setErro] = useState(false)
  const [pending, setPending] = useState(false)
  const [progresso, setProgresso] = useState<ImportarCargosProgresso | null>(null)
  const [resultado, setResultado] = useState<ResultadoImport | null>(null)

  function resetFileState() {
    setResultado(null)
    setProgresso(null)
    setMsg('')
    setErro(false)
  }

  async function executarDescritivo() {
    if (!file) {
      setErro(true)
      setMsg('Selecione o CSV ou XLSX do descritivo de cargos.')
      return
    }
    setPending(true)
    setErro(false)
    setMsg('Importando descritivo…')
    setProgresso(null)
    setResultado(null)
    try {
      const res = await importarDescritivoCargos(file)
      setResultado(res)
      setMsg(
        `Descritivo importado: ${res.lidos ?? 0} lidos · ${res.atualizados ?? 0} atualizados · ${res.criados ?? 0} criados` +
          (res.duplicados_removidos
            ? ` · ${res.duplicados_removidos} duplicados removidos`
            : '') +
          (res.ativos != null ? ` · ${res.ativos} ativos` : '') +
          '.',
      )
      await qc.invalidateQueries({ queryKey: ['rh', 'cargos'] })
    } catch (err) {
      setResultado(null)
      setErro(true)
      setMsg(err instanceof ApiError ? err.message : 'Falha ao importar descritivo.')
    } finally {
      setPending(false)
    }
  }

  async function executarCompetencias() {
    if (!file) {
      setErro(true)
      setMsg('Selecione o XLSX do Dicionário de Competências.')
      return
    }
    setPending(true)
    setErro(false)
    setMsg('Importando dicionário de competências…')
    setProgresso(null)
    setResultado(null)
    try {
      const res = await importarDicionarioCompetencias(file)
      setResultado(res)
      setMsg(
        `Competências importadas: ${res.lidos ?? 0} lidas · ${res.criados ?? 0} criadas · ${res.atualizados ?? 0} atualizadas` +
          (res.ativos != null ? ` · ${res.ativos} ativas` : '') +
          '.',
      )
      await qc.invalidateQueries({ queryKey: ['rh', 'competencias'] })
    } catch (err) {
      setResultado(null)
      setErro(true)
      setMsg(err instanceof ApiError ? err.message : 'Falha ao importar competências.')
    } finally {
      setPending(false)
    }
  }

  async function executarDominio() {
    if (!file) {
      setErro(true)
      setMsg('Selecione o arquivo cargos.wk1.')
      return
    }
    setPending(true)
    setErro(false)
    setMsg('Enviando arquivo…')
    setProgresso(null)
    setResultado(null)
    try {
      const res = await importarCargosArquivo(file, {
        dryRun,
        criarFaltantes,
        onProgress: (p) => {
          setProgresso(p)
          setMsg(
            `Processando lote ${p.loteAtual}/${p.lotes} — ${p.done} de ${p.total} cargos…`,
          )
        },
      })
      setResultado(res)
      setProgresso({
        done: res.total ?? res.lidos ?? 0,
        total: res.total ?? res.lidos ?? 0,
        atualizados: res.atualizados ?? 0,
        criados: res.criados ?? 0,
        nao_casados: res.nao_casados ?? 0,
        inativos: res.inativos ?? 0,
        loteAtual: 1,
        lotes: 1,
      })
      setMsg(
        res.dry_run
          ? 'Simulação concluída — nada foi gravado.'
          : 'Importação de cargos Domínio concluída.',
      )
      if (!res.dry_run) {
        await qc.invalidateQueries({ queryKey: ['rh', 'cargos'] })
      }
    } catch (err) {
      setResultado(null)
      setErro(true)
      setMsg(err instanceof ApiError ? err.message : 'Falha na importação de cargos.')
    } finally {
      setPending(false)
    }
  }

  if (!editable) {
    return (
      <section className="hi-page">
        <PageHeader title="Importar cargos" subtitle="Acesso restrito a RH/Admin." />
        <Alert tone="danger">Sem permissão para importar cargos.</Alert>
      </section>
    )
  }

  const detalhes = resultado?.detalhes || []
  const naoCasados = detalhes.filter((d) => d.status === 'nao_casado')
  const okDet = detalhes.filter((d) => d.status === 'ok' || d.status === 'criado' || d.campos?.length)
  const pct =
    progresso && progresso.total > 0
      ? Math.min(100, Math.round((progresso.done / progresso.total) * 100))
      : pending
        ? 2
        : 0

  return (
    <section className="hi-page hi-import-colab-page">
      <PageHeader
        title="Importar cargos e competências"
        subtitle="Descritivo de cargos (CSV/XLSX), dicionário de competências (XLSX) ou árvore Domínio (.wk1)."
        actions={
          <Link className="btn btn-secondary" to="/app/cargos">
            Voltar à lista
          </Link>
        }
      />

      {msg ? <Alert tone={erro ? 'danger' : 'info'}>{msg}</Alert> : null}

      <Tabs
        items={[
          { id: 'descritivo', label: 'Descritivo (planilha)' },
          { id: 'competencias', label: 'Competências' },
          { id: 'dominio', label: 'Domínio WK1' },
        ]}
        value={modo}
        onChange={(id) => {
          setModo(id as Modo)
          setFile(null)
          resetFileState()
        }}
        label="Tipo de importação"
      />

      {modo === 'descritivo' ? (
        <Section title="Descritivo de cargos">
          <Alert tone="info">
            Use a planilha <strong>Descritivo de Cargos Atualizado</strong> (.csv ou .xlsx). O
            sistema casa por nome (e CBO) com os cargos já importados do Domínio e preenche o
            descritivo + tags. Cargos só do Domínio sem linha na planilha continuam sem
            descritivo — edite manualmente ou inclua na planilha.
          </Alert>
          <form
            className="hi-filters hi-import-form"
            onSubmit={(e) => {
              e.preventDefault()
              void executarDescritivo()
            }}
          >
            <input
              type="file"
              accept=".csv,.xlsx,.xls,.xlsm"
              aria-label="Arquivo descritivo de cargos"
              disabled={pending}
              onChange={(e) => {
                setFile(e.target.files?.[0] || null)
                resetFileState()
              }}
            />
            <button type="submit" className="btn btn-primary" disabled={pending || !file}>
              {pending ? 'Importando…' : 'Importar descritivo'}
            </button>
          </form>
          <Muted>
            {file
              ? `Selecionado: ${file.name} (${Math.round(file.size / 1024)} KB)`
              : 'Ex.: Descritivo de Cargos Atualizado.csv'}
          </Muted>
        </Section>
      ) : null}

      {modo === 'competencias' ? (
        <Section title="Dicionário de competências">
          <Alert tone="info">
            Use o arquivo <strong>2. Dicionario de Competencias Atualizado.xlsx</strong>. Cada
            tópico vira uma tag (técnica ou comportamental) com descrição, comportamentos
            esperados e indicadores de baixo desempenho — usadas nos cargos e nas avaliações.
          </Alert>
          <form
            className="hi-filters hi-import-form"
            onSubmit={(e) => {
              e.preventDefault()
              void executarCompetencias()
            }}
          >
            <input
              type="file"
              accept=".xlsx,.xlsm"
              aria-label="Arquivo dicionário de competências"
              disabled={pending}
              onChange={(e) => {
                setFile(e.target.files?.[0] || null)
                resetFileState()
              }}
            />
            <button type="submit" className="btn btn-primary" disabled={pending || !file}>
              {pending ? 'Importando…' : 'Importar competências'}
            </button>
          </form>
          <Muted>
            {file
              ? `Selecionado: ${file.name} (${Math.round(file.size / 1024)} KB)`
              : 'Ex.: 2. Dicionario de Competencias Atualizado.xlsx'}
          </Muted>
        </Section>
      ) : null}

      {modo === 'dominio' ? (
        <Section title="Arquivo Domínio">
          <form
            className="hi-filters hi-import-form"
            onSubmit={(e) => {
              e.preventDefault()
              void executarDominio()
            }}
          >
            <input
              type="file"
              accept=".wk1"
              aria-label="Arquivo de cargos Domínio"
              disabled={pending}
              onChange={(e) => {
                setFile(e.target.files?.[0] || null)
                resetFileState()
              }}
            />
            <label className="hi-check">
              <input
                type="checkbox"
                checked={dryRun}
                disabled={pending}
                onChange={(e) => setDryRun(e.target.checked)}
              />
              Simular (dry-run)
            </label>
            <label className="hi-check">
              <input
                type="checkbox"
                checked={criarFaltantes}
                disabled={pending}
                onChange={(e) => setCriarFaltantes(e.target.checked)}
              />
              Criar quem não existir
            </label>
            <button type="submit" className="btn btn-primary" disabled={pending || !file}>
              {pending ? 'Importando…' : dryRun ? 'Simular importação' : 'Importar Domínio'}
            </button>
          </form>
          <Muted>
            {file
              ? `Selecionado: ${file.name} (${Math.round(file.size / 1024)} KB)`
              : 'Ex.: cargos.wk1 (Domínio)'}
            {' · '}
            Grava nome, CBO, atividade e snapshot Domínio (sem descritivo completo).
          </Muted>

          {(pending || progresso) && (
            <div className="hi-import-progress" aria-live="polite">
              <div className="hi-import-progress-meta">
                <span>
                  {progresso
                    ? `${progresso.done} / ${progresso.total} (${pct}%)`
                    : 'Preparando…'}
                </span>
                {progresso ? (
                  <span>
                    Lote {progresso.loteAtual}/{progresso.lotes}
                  </span>
                ) : null}
              </div>
              <div
                className="hi-import-progress-bar"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={pct}
              >
                <div className="hi-import-progress-fill" style={{ width: `${pct}%` }} />
              </div>
              {progresso ? (
                <Muted>
                  Atualizados {progresso.atualizados} · Criados {progresso.criados} · Sem match{' '}
                  {progresso.nao_casados} · Inativos {progresso.inativos}
                </Muted>
              ) : null}
            </div>
          )}
        </Section>
      ) : null}

      {resultado ? (
        <>
          <KpiGrid>
            <Kpi label="Lidos" value={String(resultado.lidos ?? resultado.total ?? 0)} />
            <Kpi label="Atualizados" value={String(resultado.atualizados ?? 0)} />
            <Kpi label="Criados" value={String(resultado.criados ?? 0)} />
            {modo === 'dominio' ? (
              <>
                <Kpi label="Inativos" value={String(resultado.inativos ?? 0)} />
                <Kpi label="Sem match" value={String(resultado.nao_casados ?? 0)} />
              </>
            ) : (
              <Kpi label="Ativos" value={String(resultado.ativos ?? '—')} />
            )}
          </KpiGrid>

          {modo === 'dominio' ? (
            <Section title="Detalhes" flush>
              {!detalhes.length ? <Empty>Sem detalhes neste lote.</Empty> : null}
              {okDet.length > 0 ? (
                <div className="hi-import-detalhes">
                  <Muted>Atualizações / criações (amostra)</Muted>
                  <ul>
                    {okDet.slice(0, 40).map((d, i) => (
                      <li key={`ok-${i}`}>
                        <strong>{d.nome}</strong>
                        {d.codigo ? ` [${d.codigo}]` : ''}
                        {d.campos?.length ? ` — ${d.campos.join(', ')}` : ''}
                        {d.match ? ` (${d.match})` : ''}
                        {d.ativo === false ? ' · inativo' : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {naoCasados.length > 0 ? (
                <div className="hi-import-detalhes">
                  <Muted>Sem match (amostra)</Muted>
                  <ul>
                    {naoCasados.slice(0, 40).map((d, i) => (
                      <li key={`nc-${i}`}>
                        {d.nome}
                        {d.codigo ? ` [${d.codigo}]` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </Section>
          ) : modo === 'competencias' ? (
            <Section title="Próximo passo">
              <Muted>
                As tags ficam disponíveis ao editar cargos e no banco de competências. Importe o
                descritivo de cargos em seguida para vincular as tags aos cargos da planilha.
              </Muted>
              <div className="hi-actions">
                <Link className="btn btn-primary" to="/app/cargos">
                  Ver cargos
                </Link>
              </div>
            </Section>
          ) : (
            <Section title="Próximo passo">
              <Muted>
                Abra um cargo Domínio (ex.: ADMINISTRATIVO) e confira se objetivo/tags foram
                preenchidos. Se o nome na planilha for diferente (ex.: “Analista Administrativo”),
                o sistema cria/atualiza esse nome — use a lista com filtro “Com colaboradores”
                para ver quem ainda ficou sem descritivo.
              </Muted>
              <div className="hi-actions">
                <Link className="btn btn-primary" to="/app/cargos">
                  Ver cargos
                </Link>
              </div>
            </Section>
          )}
        </>
      ) : null}
    </section>
  )
}
